/**
 * apps/web :: lib/content-source
 *
 * The content-source seam (spec §13 drift mitigation). The render components
 * (LessonPage, ContentBlock, CountryDiffCard) consume LessonDetail and MUST
 * NOT be forked for preview. Instead we vary only the *source* of the
 * normalized lesson:
 *   - bundledContentSource  : the static catalogue (@/data/training-lessons).
 *                             This is the runtime/default path — unchanged.
 *   - previewContentSource  : fetches a normalized StaticLessonContent from the
 *                             authoring-plane preview API (draft via Directus
 *                             token, or a past prod release via release_id).
 *
 * Sources return StaticLessonContent (LessonDetail minus `completed`);
 * training-api composes the per-device `completed` flag on top, identically
 * for both sources, so the render path never knows which source it got.
 */
import type { Course, StaticLessonContent } from "@/lib/training-api";
import { getLessons as getBundledLessons } from "@/data/training-lessons";
import { apiFetch } from "@/lib/api";

export interface ContentSource {
  getLessonDetail(id: string, locale: string): Promise<StaticLessonContent>;
  listLessons(locale: string): Promise<StaticLessonContent[]>;
  // Courses are backend-only (no bundled catalogue), so these are optional —
  // training-api treats their absence as "no courses".
  listCourses?(locale: string): Promise<Course[]>;
  getCourse?(id: string, locale: string): Promise<Course | null>;
}

export const bundledContentSource: ContentSource = {
  async getLessonDetail(id, locale) {
    const lesson = getBundledLessons(locale).find((l) => l.id === id);
    if (!lesson) throw new Error(`Lesson not found: ${id}`);
    return lesson;
  },
  async listLessons(locale) {
    return getBundledLessons(locale);
  },
};

// ── Live backend source ──────────────────────────────────────────────────────
//
// Reads the entitlement-gated catalogue from the Ktor backend `/api/training/*`
// (ADR-0019), with the rb_session cookie (apiFetch → credentials: include). The
// backend response is the kmp `LessonDetailResponse` shape, which differs from
// the FE StaticLessonContent in a few field names — adapt here. On any failure
// (offline, 401 for a signed-out visitor, 5xx) we fall back to the bundled
// catalogue so the training surface always renders something.

/** The backend `LessonDetailResponse` wire shape (snake_case, + gate fields). */
interface BackendLesson {
  id: string;
  title: string;
  category: string;
  duration_min: number;
  emoji: string;
  color_from: string;
  color_to: string;
  summary: string;
  tags?: string[];
  content?: StaticLessonContent["content"] | null;
  related_lesson_ids?: string[];
  locked?: boolean;
  requires_upgrade_to?: string | null;
}

function toStaticLesson(r: BackendLesson): StaticLessonContent {
  return {
    id: r.id,
    title: r.title,
    category: r.category as StaticLessonContent["category"],
    duration_min: r.duration_min,
    emoji: r.emoji,
    colorFrom: r.color_from,
    colorTo: r.color_to,
    summary: r.summary,
    tags: r.tags ?? [],
    // Locked lessons return content: null (gate); the lesson page shows a paywall.
    content: r.content ?? [],
    // The migrated lessons carry no country diffs yet (separate from the lesson doc).
    country_diffs: [],
    related_lesson_ids: r.related_lesson_ids ?? [],
    locked: r.locked ?? false,
    requires_upgrade_to: r.requires_upgrade_to ?? null,
  };
}

export function liveContentSource(): ContentSource {
  const q = (locale: string) => `?locale=${encodeURIComponent(locale)}`;
  return {
    async listLessons(locale) {
      try {
        const data = await apiFetch<{ lessons: BackendLesson[] }>(
          `/api/training/lessons${q(locale)}`,
        );
        return (data.lessons ?? []).map(toStaticLesson);
      } catch {
        // Signed-out (401) or backend hiccup → caller falls back to bundled.
        return [];
      }
    },
    async getLessonDetail(id, locale) {
      try {
        const r = await apiFetch<BackendLesson>(
          `/api/training/lessons/${encodeURIComponent(id)}${q(locale)}`,
        );
        return toStaticLesson(r);
      } catch {
        return bundledContentSource.getLessonDetail(id, locale);
      }
    },
    // CourseDetailResponse maps 1:1 to the FE Course (same snake_case keys), so
    // these pass through. On error → empty/null (no bundled course catalogue).
    async listCourses(locale) {
      try {
        const data = await apiFetch<{ courses: Course[] }>(`/api/training/courses${q(locale)}`);
        return data.courses ?? [];
      } catch {
        return [];
      }
    },
    async getCourse(id, locale) {
      try {
        return await apiFetch<Course>(`/api/training/courses/${encodeURIComponent(id)}${q(locale)}`);
      } catch {
        return null;
      }
    },
  };
}

export interface PreviewSourceConfig {
  baseUrl: string;
  token: string | null;
  releaseId: string | null;
}

export function previewContentSource(config: PreviewSourceConfig): ContentSource {
  function buildUrl(id: string, locale: string): string {
    const base = config.baseUrl.replace(/\/$/, "");
    const qs = new URLSearchParams();
    qs.set("locale", locale);
    // The draft token is a secret and rides in the Authorization header, never
    // the query string (query strings leak into logs/history). release_id is a
    // public identifier and is fine as a query param.
    if (config.releaseId) qs.set("release_id", config.releaseId);
    return `${base}/preview/lesson/${encodeURIComponent(id)}?${qs.toString()}`;
  }

  async function fetchLesson(id: string, locale: string): Promise<StaticLessonContent> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (config.token) headers.Authorization = `Bearer ${config.token}`;
    const res = await fetch(buildUrl(id, locale), {
      headers,
      // The preview API sits behind Cloudflare Access; the Access cookie/JWT
      // rides along same-origin within the authoring plane.
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error(`preview source: HTTP ${res.status} for ${id}`);
    }
    return (await res.json()) as StaticLessonContent;
  }

  return {
    getLessonDetail: (id, locale) => fetchLesson(id, locale),
    async listLessons(locale) {
      // Single-element preview is the Live-Preview use case; a list endpoint
      // is added when an author previews the whole catalogue. For now resolve
      // the requested-lesson-only contract via the caller; return empty so the
      // related-lessons strip degrades gracefully in preview.
      void locale;
      return [];
    },
  };
}
